import { HttpBackend, HttpClient, HttpParams } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { JsonConvert } from "json2typescript";
import { Observable } from "rxjs";
import { catchError, map } from "rxjs/operators";

import { FeatureFlagResponse } from "@hermes/api-model-feature-flag";
import { LOCALE, Settings } from "@hermes/core-app-core";

import {
    FeatureFlagDefinition,
    FeatureFlagName,
} from "../constants/feature-flags.constant";
import { FeatureFlags } from "../reducers/feature-flag.state";

@Injectable()
export class FeatureFlagService {
    private locale = inject(LOCALE);
    private http = inject(HttpBackend);
    private settings = inject(Settings);

    private httpClient: HttpClient;

    constructor() {
        const http = this.http;

        /**
         * We use the HttpBackend provider to make direct HTTP calls, bypassing interceptors.
         * This is necessary because interceptors may depend on feature flags that this service retrieves.
         * So to avoid interdependencies, we use HttpBackend instead.
         */
        this.httpClient = new HttpClient(http);
    }

    /**
     * retrieve a list of values for given feature flags.
     */
    public featureFlagCall(
        flags: FeatureFlagDefinition[],
    ): Observable<FeatureFlags> {
        // construct final request
        let params: HttpParams = new HttpParams();
        flags.forEach((flag) => {
            params = params.append("features", `${flag.flagName}`);
            params = params.append("values", `${flag.defaultValue}`);
        });
        // Need to add locale params since we don't got through locale interceptor
        params = params.append("locale", this.locale.code);
        return this.httpClient
            .get(`${this.settings.apiUrl}/feature-flag`, {
                params,
            })
            .pipe(
                map((response: unknown) =>
                    new JsonConvert().deserializeObject(
                        response,
                        FeatureFlagResponse,
                    ),
                ),
                map((featureFlagResponse) => {
                    const featureFlagValues: Partial<FeatureFlags> = {};
                    featureFlagResponse.featureFlags.forEach(
                        ({ id, value }) => {
                            featureFlagValues[id as FeatureFlagName] =
                                value as boolean;
                        },
                    );
                    return featureFlagValues as FeatureFlags;
                }),

                catchError((error) => {
                    throw new Error(
                        `Feature flag resolver error: ${error.message.toString()}`,
                    );
                }),
            );
    }
}
